/**
 * React Query hook for workspace list
 *
 * Provides data fetching for all workspaces with caching support.
 */

import { useQuery } from '@tanstack/react-query';
import { workspaceApi } from '../services/workspaceApi';
import type { Workspace } from '../types/workspace';

/**
 * Query key factory for workspaces list
 */
export const workspacesKeys = {
  all: ['workspaces'] as const,
  list: () => ['workspaces', 'list'] as const,
};

/**
 * Hook return type for useWorkspaces
 */
export interface UseWorkspacesResult {
  workspaces: Workspace[];
  isLoading: boolean;
  isFetching: boolean;
  error: Error | null;
  refetch: () => Promise<void>;
}

/**
 * React Query hook for fetching workspace list
 *
 * @returns Workspaces data sorted alphabetically, loading states, and refetch function
 *
 * @example
 * ```tsx
 * const { workspaces, isLoading, error, refetch } = useWorkspaces();
 * ```
 */
export function useWorkspaces(): UseWorkspacesResult {
  const {
    data,
    isLoading,
    isFetching,
    error,
    refetch: queryRefetch,
  } = useQuery({
    queryKey: workspacesKeys.list(),
    queryFn: async () => {
      const workspaces = await workspaceApi.listWorkspaces();
      // Sort alphabetically by name
      return workspaces.sort((a, b) => a.name.localeCompare(b.name));
    },
  });

  const refetch = async () => {
    await queryRefetch();
  };

  return {
    workspaces: data ?? [],
    isLoading,
    isFetching,
    error: error as Error | null,
    refetch,
  };
}
